// src/components/access/AuthGate.tsx
// Component for requiring authentication before rendering protected content

import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useAuth } from "@/context/AuthContext";

type AuthGateProps = {
  children: ReactNode;
};

/**
 * AuthGate: Require user to be signed in before accessing content.
 * If not authenticated, redirects to /login with return URL preserved.
 *
 * Usage:
 * <Route
 *   path="/app/*"
 *   element={
 *     <AuthGate>
 *       <MainAppLayout />
 *     </AuthGate>
 *   }
 * />
 */
export function AuthGate({ children }: AuthGateProps) {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  // Show loading splash while checking auth status
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="mb-4 text-4xl">⏳</div>
          <div className="text-sm text-[var(--text-muted)]">
            Loading your Nexus workspace…
          </div>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ redirectTo: location.pathname + location.search }}
      />
    );
  }

  // User is authenticated, render children
  return <>{children}</>;
}
